import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { scoreTextOn, scoreToColor } from './areaData.js';
import { track } from '../analytics/events.js';
import { MAX_COMPARED, shareArea, shareComparison } from './areaShare.js';
import {
    COMPARE_ROWS, compareArea, compareTops, fetchAreaHomesTotal, fetchAreaTrend, formatScore, formatTrend,
} from './compareData.js';
import {
    clearComparedAreas, removeComparedArea, reorderComparedAreas, useCompareHover, useComparedAreas,
} from './compareAreas.js';
import useColumnDrag from './useColumnDrag.js';
import getTooltipData from './tooltipData.js';
import './compareCard.css';

// The comparison card (design A "Kolumner" of the 2026-11-01 "Jämföra områden" canvas): every area double-clicked
// in either view, a column each, numbered like their markers on the map, with the six numbers, the price trend
// and the homes for sale in rows and the best of each marked (Högst, Flest, Lägst). The area under the pointer
// shows in a dashed column after them ("Pekar på"), so it can be weighed before it's added. That column is always
// there (design 1 of "Kortet när man pekar runt" on the canvas, the owner's pick): pointing at nothing it waits
// ("Nästa område · Peka på ett område"), so the card never changes size as the pointer moves. A few columns show
// at once (kartvy up to five, listvy's panel two); the rest scroll sideways while the measures' names stay put.
// "Jämför i storvy" opens the comparison's own page over the view and Dela shares it; "Visa bostäder" is only on
// that page (the owner's wish).
//
// variant: 'map' (kartvy on desktop: hugs its columns), 'panel' (listvy's panel under the map) or 'sheet' (phones);
// the last two fill their width. maxVisible: how many columns show before the rest scroll. embedded: inside a sheet
// that has its own title and close (listvy on phones), so the card has no header and a "Rensa" in its footer.
// The panel is compact, at the panel's fixed height (listvy's map keeps its size whatever the panel shows): no
// header, the end-the-comparison ✕ in the footer, one line per measure. All variants are dense (design 1 "Tätare"
// of the canvas's "Ett lägre kort", the owner's pick, 2026-11-01: the card covered too much of the map): a column
// head is two lines (number and name; score, word and ✕) and the measures sit close; on the map the buttons are in
// the header, so there's no footer. On phones one area alone is its ordinary area card (the hover card's, with Dela)
// and a ✕, not a table of one column (the owner, 2026-11-01); the columns come with the second area. A column can
// be dragged by its area's name to another place (useColumnDrag.js; the owner, 2026-11-01): the comparison's own
// order changes, so the map's numbers follow.

const COPIED_MS = 2200;
const SIZES = {
    map: { label: 96, col: 142 },
    panel: { label: 86, col: 108 },
    sheet: { label: 86, col: 124 },
};
const GHOST_SETTLE_MS = 180;

const CloseIcon = ({ size = 11 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
    </svg>
);
const CompareIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="7.5" height="16" rx="1.8" />
        <rect x="13.5" y="4" width="7.5" height="16" rx="1.8" />
    </svg>
);
const ShareIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15V3" />
        <path d="M7.5 7.5L12 3l4.5 4.5" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
);

const homesText = (count, swedish) => {
    if (count == null) return '–';
    return swedish ? `${count} ${count === 1 ? 'bostad' : 'bostäder'}` : `${count} ${count === 1 ? 'home' : 'homes'}`;
};

export default function CompareCard({
    swedish, colorCache, areaStats, variant = 'map', maxVisible = 5, showGhost = false, view = 'kartvy',
    onPreview = null, onDismiss = null, embedded = false, className = '',
}) {
    const compared = useComparedAreas();
    const hover = useCompareHover();
    const sizes = SIZES[variant] || SIZES.map;
    const compact = variant === 'panel';
    const [folded, setFolded] = useState(false);
    const [copied, setCopied] = useState(false);

    // Folded, the card is a strip of the areas (design 3's, the owner's pick with 1); an area added then shows as a
    // chip, so the fold is kept. A new comparison starts unfolded.
    const comparedCount = compared.length;
    useEffect(() => {
        if (!comparedCount) setFolded(false);
    }, [comparedCount]);

    const areas = useMemo(
        () => compared.map((item) => compareArea(item.code, item.name, colorCache, areaStats, swedish)).filter(Boolean),
        [compared, colorCache, areaStats, swedish],
    );
    const tops = useMemo(() => compareTops(areas), [areas]);

    // The area under the pointer, once it has stayed a moment (sweeping across the map doesn't fetch for every one).
    // It stays until another area has been pointed at (not on a compared one, which is lit in its own column).
    const [pointed, setPointed] = useState(null); // { code, name }
    const hoverCode = showGhost && hover && !compared.some((item) => item.code === hover.code) ? hover.code : null;
    const hoverName = hover?.name || '';
    useEffect(() => {
        if (!hoverCode) return undefined;
        const timer = setTimeout(() => setPointed({ code: hoverCode, name: hoverName }), GHOST_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [hoverCode, hoverName]);
    const ghost = useMemo(
        () => (pointed && !compared.some((item) => item.code === pointed.code)
            ? compareArea(pointed.code, pointed.name, colorCache, areaStats, swedish)
            : null),
        [pointed, compared, colorCache, areaStats, swedish],
    );
    // Its column, kept whether or not something is pointed at; none once the comparison is full.
    const slot = showGhost && compared.length < MAX_COMPARED;
    // Kartvy on phones with one area: the area's own card instead of the columns.
    const single = variant === 'sheet' && !embedded && areas.length === 1;
    const singleMarkup = useMemo(
        () => (single ? getTooltipData(areas[0].name, areas[0].code, colorCache, areaStats, scoreToColor, { swedish }, { shareable: true }) : ''),
        [single, areas, colorCache, areaStats, swedish],
    );

    // The price trends and homes for sale, fetched once per area (cached in compareData.js).
    const [trends, setTrends] = useState({});
    const [homes, setHomes] = useState({});
    const codesKey = [...areas.map((area) => area.code), ghost?.code].filter(Boolean).join(',');
    useEffect(() => {
        let cancelled = false;
        codesKey.split(',').filter(Boolean).forEach((code) => {
            fetchAreaTrend(code).then((trend) => {
                if (!cancelled) setTrends((prev) => (code in prev ? prev : { ...prev, [code]: trend }));
            });
            fetchAreaHomesTotal(code).then((total) => {
                if (!cancelled) setHomes((prev) => (prev[code] === total ? prev : { ...prev, [code]: total }));
            });
        });
        return () => { cancelled = true; };
    }, [codesKey]);

    // Filling variants share their width between the columns on show; the map's card hugs its columns.
    const scrollRef = useRef(null);
    const [width, setWidth] = useState(0);
    const fills = variant !== 'map';
    const hasAreas = areas.length > 0;
    useLayoutEffect(() => {
        const element = scrollRef.current;
        if (!fills || !element) return undefined;
        // The room inside the scroller's side padding, or columns that all fit still scroll by that much.
        const update = () => {
            const style = getComputedStyle(element);
            setWidth(element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
        };
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [fills, folded, hasAreas, single]);
    // A fade at the right edge while more columns are hidden there.
    const [moreRight, setMoreRight] = useState(false);
    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return undefined;
        const update = () => setMoreRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 4);
        update();
        element.addEventListener('scroll', update, { passive: true });
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        observer?.observe(element);
        if (element.firstElementChild) observer?.observe(element.firstElementChild);
        return () => {
            element.removeEventListener('scroll', update);
            observer?.disconnect();
        };
    }, [folded, hasAreas, single]);
    // A column dragged by its name to another place: the columns show in the drag's order while it's held.
    const columnDrag = useColumnDrag({
        codes: areas.map((area) => area.code),
        scrollRef,
        enabled: areas.length > 1 && !single && !folded,
        onReorder: (order) => {
            reorderComparedAreas(order);
            track('compare.reorder', { count: order.length, source: 'card', view });
        },
    });
    const shown = columnDrag.order.map((code) => areas.find((area) => area.code === code)).filter(Boolean);
    const dragged = (code) => (columnDrag.dragging === code ? ' is-dragging' : '');
    const columns = areas.length + (slot ? 1 : 0);
    let colWidth = sizes.col;
    if (fills && width > 0) {
        const shown = Math.max(1, Math.min(columns, maxVisible));
        colWidth = Math.max(sizes.col - 14, Math.floor((width - sizes.label) / shown));
    }

    // The map's card publishes its height, so kartvy's hover card can sit above it (compareCard.css).
    const rootRef = useRef(null);
    useLayoutEffect(() => {
        // Kartvy's card (desktop and phones); not listvy's panel or sheet.
        if (variant === 'panel' || embedded || !hasAreas) return undefined;
        const element = rootRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return undefined;
        const body = document.body;
        const observer = new ResizeObserver(() => body.style.setProperty('--compare-card-h', `${element.offsetHeight}px`));
        observer.observe(element);
        body.classList.add('is-comparing');
        return () => {
            observer.disconnect();
            body.classList.remove('is-comparing');
            body.style.removeProperty('--compare-card-h');
        };
    }, [variant, embedded, hasAreas]);

    if (!areas.length) return null;

    const share = () => {
        const done = (result) => {
            if (result !== 'copied') return;
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_MS);
        };
        if (areas.length === 1) {
            shareArea({ code: areas[0].code, name: areas[0].name, source: 'compare_card' }).then(done);
        } else {
            shareComparison({ codes: areas.map((area) => area.code), names: areas.map((area) => area.name), source: 'card' }).then(done);
        }
    };

    const title = areas.length === 1
        ? (swedish ? '1 område' : '1 area')
        : (swedish ? `Jämför ${areas.length} områden` : `Comparing ${areas.length} areas`);
    const hint = areas.length === 1
        ? (swedish ? 'Dubbelklicka på fler områden för att jämföra' : 'Double-click more areas to compare')
        : (swedish ? 'Dubbelklicka på fler för att lägga till' : 'Double-click more to add them');
    const tableWidth = sizes.label + (columns * colWidth);
    // The number leads the name, except in listvy's narrow panel, where it leads the second line instead.
    const numFirst = !compact;
    // Kartvy's card has its buttons in the header, so it has no footer row.
    const headButtons = variant === 'map' && !embedded;
    const canPreview = areas.length >= 2 && onPreview;
    const previewAll = () => onPreview(areas.map((area) => area.code));
    const shareLong = areas.length === 1
        ? (swedish ? 'Dela området' : 'Share the area')
        : (swedish ? 'Dela jämförelsen' : 'Share the comparison');

    // The slot's cells while nothing is pointed at.
    const waitingCell = (key) => (
        <td key={key} className="is-ghost is-waiting" aria-hidden="true">
            <span className="compare-card-value"><b>·</b></span>
        </td>
    );

    const valueCell = (area, row, isGhost) => {
        const fact = area.facts[row.key];
        const top = !isGhost && tops[row.key]?.has(area.code);
        return (
            <td key={area.code} className={`${isGhost ? 'is-ghost' : ''}${dragged(area.code)}`.trim() || undefined}>
                <span className="compare-card-value">
                    <b>{fact?.value || '–'}</b>
                    {fact?.score != null && <i className="compare-card-dot" style={{ background: fact.color }} />}
                    {top && <span className="compare-card-top">{row.top[swedish ? 'sv' : 'en']}</span>}
                </span>
            </td>
        );
    };

    return (
        <section
            ref={rootRef}
            className={`compare-card compare-card--${variant} is-dense${single ? ' is-single' : ''}${folded && !single ? ' is-folded' : ''}${embedded ? ' is-embedded' : ''}${className ? ` ${className}` : ''}`}
            aria-label={swedish ? 'Jämförelse av områden' : 'Area comparison'}
            style={{ '--cc-label-w': `${sizes.label}px`, '--cc-col-w': `${colWidth}px`, '--cc-max-cols': maxVisible }}
        >
            {single && (
                <>
                    {/* The card is an HTML string (tooltipData.js); its Dela and price trend fill in by themselves. */}
                    <div className="compare-card-single" dangerouslySetInnerHTML={{ __html: singleMarkup }} />
                    <button
                        type="button"
                        className="compare-card-icon-btn compare-card-single-close"
                        onClick={() => clearComparedAreas({ view })}
                        aria-label={swedish ? 'Stäng' : 'Close'}
                    >
                        <CloseIcon />
                    </button>
                    <p className="compare-card-single-hint">
                        {swedish ? 'Dubbeltryck på ett område till för att jämföra' : 'Double-tap another area to compare'}
                    </p>
                </>
            )}
            {!single && !embedded && !compact && (
            <header className="compare-card-head">
                <b className="compare-card-title">{folded ? (swedish ? 'Jämför' : 'Compare') : title}</b>
                {variant === 'map' && !folded && <span className="compare-card-hint">{hint}</span>}
                {folded && (
                    <span className="compare-card-mini">
                        {areas.map((area, index) => (
                            <span
                                key={area.code}
                                className={`compare-card-chip${hover?.code === area.code ? ' is-hovered' : ''}`}
                            >
                                <i>{index + 1}</i>
                                <b>{area.name}</b>
                                {area.score != null && (
                                    <span className="compare-card-score" style={{ background: area.colour, color: scoreTextOn(area.colour) }}>
                                        {formatScore(area.score, swedish)}
                                    </span>
                                )}
                            </span>
                        ))}
                        {/* The area under the pointer, at a fixed width so the strip doesn't change size. */}
                        {slot && (
                            <span className={`compare-card-chip is-ghost${ghost ? '' : ' is-waiting'}`}>
                                <i aria-hidden="true" />
                                <b title={ghost?.name}>{ghost ? ghost.name : (swedish ? 'Peka på ett område' : 'Point at an area')}</b>
                                {ghost?.score != null && (
                                    <span className="compare-card-score" style={{ background: ghost.colour, color: scoreTextOn(ghost.colour) }}>
                                        {formatScore(ghost.score, swedish)}
                                    </span>
                                )}
                            </span>
                        )}
                    </span>
                )}
                <span className="compare-card-head-actions">
                    {headButtons && canPreview && !folded && (
                        <button type="button" className="compare-card-head-btn is-primary" onClick={previewAll}>
                            <CompareIcon />
                            {swedish ? 'Jämför i storvy' : 'Compare in full view'}
                        </button>
                    )}
                    {headButtons && !folded && (
                        <button
                            type="button"
                            className={`compare-card-head-btn${copied ? ' is-copied' : ''}`}
                            onClick={share}
                            title={shareLong}
                        >
                            <ShareIcon />
                            {copied ? (swedish ? 'Kopierad!' : 'Copied!') : (swedish ? 'Dela' : 'Share')}
                        </button>
                    )}
                    {onDismiss && (
                        <button type="button" className="compare-card-text-btn" onClick={() => clearComparedAreas({ view })}>
                            {swedish ? 'Rensa' : 'Clear'}
                        </button>
                    )}
                    {!onDismiss && folded && (
                        <button
                            type="button"
                            className="compare-card-unfold"
                            onClick={() => setFolded(false)}
                            aria-expanded="false"
                        >
                            <ChevronIcon />
                            {swedish ? 'Visa siffrorna' : 'Show the numbers'}
                        </button>
                    )}
                    {!onDismiss && !folded && (
                        <button
                            type="button"
                            className="compare-card-icon-btn compare-card-fold"
                            onClick={() => setFolded(true)}
                            aria-expanded="true"
                            aria-label={swedish ? 'Fäll ihop jämförelsen' : 'Fold the comparison'}
                        >
                            <ChevronIcon />
                        </button>
                    )}
                    <button
                        type="button"
                        className="compare-card-icon-btn"
                        onClick={() => (onDismiss ? onDismiss() : clearComparedAreas({ view }))}
                        aria-label={onDismiss
                            ? (swedish ? 'Stäng' : 'Close')
                            : (swedish ? 'Avsluta jämförelsen' : 'End the comparison')}
                    >
                        <CloseIcon />
                    </button>
                </span>
            </header>
            )}

            {!single && !folded && (
                <>
                    <div className={`compare-card-scroll${moreRight ? ' has-more' : ''}`} ref={scrollRef}>
                        <table className="compare-card-table" style={{ width: `${tableWidth}px` }}>
                            <colgroup>
                                <col style={{ width: `${sizes.label}px` }} />
                                {shown.map((area) => <col key={area.code} style={{ width: `${colWidth}px` }} />)}
                                {slot && <col style={{ width: `${colWidth}px` }} />}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="compare-card-corner" aria-hidden="true" />
                                    {shown.map((area, index) => (
                                        <th
                                            key={area.code}
                                            scope="col"
                                            data-compare-col={area.code}
                                            className={`compare-card-area${hover?.code === area.code ? ' is-hovered' : ''}${dragged(area.code)}`}
                                        >
                                            <div className="compare-card-area-box">
                                            <span
                                                className={`compare-card-area-top${areas.length > 1 ? ' is-handle' : ''}`}
                                                title={areas.length > 1 ? `${area.name} · ${swedish ? 'Dra för att flytta' : 'Drag to move'}` : area.name}
                                                {...columnDrag.handleProps(area.code)}
                                            >
                                                {numFirst && <i className="compare-card-num">{index + 1}</i>}
                                                <b className="compare-card-name">{area.name}</b>
                                            </span>
                                            <span className="compare-card-area-sub">
                                                {!numFirst && <i className="compare-card-num">{index + 1}</i>}
                                                {area.score != null && (
                                                    <span className="compare-card-score" style={{ background: area.colour, color: scoreTextOn(area.colour) }}>
                                                        {formatScore(area.score, swedish)}
                                                    </span>
                                                )}
                                                <span className="compare-card-word">
                                                    {area.word || (swedish ? 'Inget betyg' : 'No score')}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="compare-card-remove"
                                                    onClick={() => removeComparedArea(area.code, { view })}
                                                    aria-label={swedish ? `Ta bort ${area.name} ur jämförelsen` : `Remove ${area.name} from the comparison`}
                                                >
                                                    <CloseIcon size={10} />
                                                </button>
                                            </span>
                                            </div>
                                        </th>
                                    ))}
                                    {slot && (
                                        <th scope="col" className={`compare-card-area is-ghost${ghost ? '' : ' is-waiting'}`}>
                                            <div className="compare-card-area-box">
                                            <span className="compare-card-area-top">
                                                {numFirst && <i className="compare-card-num is-ghost" aria-hidden="true" />}
                                                <b className="compare-card-name" title={ghost?.name}>
                                                    {ghost ? ghost.name : (swedish ? 'Nästa område' : 'Next area')}
                                                </b>
                                            </span>
                                            <span className="compare-card-area-sub">
                                                {!numFirst && <i className="compare-card-num is-ghost" aria-hidden="true" />}
                                                {ghost?.score != null && (
                                                    <span className="compare-card-score" style={{ background: ghost.colour, color: scoreTextOn(ghost.colour) }}>
                                                        {formatScore(ghost.score, swedish)}
                                                    </span>
                                                )}
                                                <span className="compare-card-word">
                                                    {ghost
                                                        ? (swedish ? 'Pekar på' : 'Pointing at')
                                                        : (swedish ? 'Peka på ett område' : 'Point at an area')}
                                                </span>
                                            </span>
                                            </div>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARE_ROWS.map((row) => (
                                    <tr key={row.key}>
                                        <th scope="row"><span className="compare-card-label">{row[swedish ? 'sv' : 'en']}</span></th>
                                        {shown.map((area) => valueCell(area, row, false))}
                                        {slot && (ghost ? valueCell(ghost, row, true) : waitingCell(`wait-${row.key}`))}
                                    </tr>
                                ))}
                                <tr>
                                    <th scope="row"><span className="compare-card-label">{swedish ? 'Priser, 3 år' : 'Prices, 3 years'}</span></th>
                                    {[...shown, ...(ghost ? [ghost] : [])].map((area) => {
                                        const trend = trends[area.code];
                                        return (
                                            <td key={area.code} className={area === ghost ? 'is-ghost' : (dragged(area.code).trim() || undefined)}>
                                                <span className={`compare-card-value compare-card-trend${trend && trend.change < 0 ? ' is-down' : ''}`}>
                                                    <b>{trend ? formatTrend(trend, swedish) : '–'}</b>
                                                </span>
                                            </td>
                                        );
                                    })}
                                    {slot && !ghost && waitingCell('wait-trend')}
                                </tr>
                                <tr>
                                    <th scope="row"><span className="compare-card-label">{swedish ? 'Till salu' : 'For sale'}</span></th>
                                    {[...shown, ...(ghost ? [ghost] : [])].map((area) => (
                                        <td key={area.code} className={area === ghost ? 'is-ghost' : (dragged(area.code).trim() || undefined)}>
                                            <span className="compare-card-value"><b>{homesText(homes[area.code], swedish)}</b></span>
                                        </td>
                                    ))}
                                    {slot && !ghost && waitingCell('wait-homes')}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {!headButtons && (
                    <footer className="compare-card-foot">
                        {embedded && (
                            <button type="button" className="compare-card-text-btn compare-card-clear" onClick={() => clearComparedAreas({ view })}>
                                {swedish ? 'Rensa' : 'Clear'}
                            </button>
                        )}
                        {canPreview && (
                            <button type="button" className="compare-card-btn is-primary" onClick={previewAll}>
                                <CompareIcon />
                                {swedish ? 'Jämför i storvy' : 'Compare in full view'}
                            </button>
                        )}
                        <button type="button" className={`compare-card-btn${copied ? ' is-copied' : ''}`} onClick={share}>
                            <ShareIcon />
                            {copied
                                ? (swedish ? 'Länk kopierad!' : 'Link copied!')
                                : shareLong}
                        </button>
                        {compact && (
                            <button
                                type="button"
                                className="compare-card-icon-btn compare-card-end"
                                onClick={() => clearComparedAreas({ view })}
                                aria-label={swedish ? 'Avsluta jämförelsen' : 'End the comparison'}
                                title={swedish ? 'Avsluta jämförelsen' : 'End the comparison'}
                            >
                                <CloseIcon />
                            </button>
                        )}
                    </footer>
                    )}
                </>
            )}
        </section>
    );
}
